export const MAX_SECTIONS = 30;
export const MAX_PURPOSE_CHARS = 500;
export const MAX_PROMPT_CHARS = 4500;

export interface TemplateSection {
  title: string;
  purpose: string;
  prompt: string;
  order: number;
  sectionType?: "snapshot" | "docList";
  config?: {
    excludedCategoryNames?: string[];
  };
}

export const isSectionOverCharLimit = (s: TemplateSection): boolean =>
  s.sectionType !== "docList" &&
  (s.purpose.length > MAX_PURPOSE_CHARS || s.prompt.length > MAX_PROMPT_CHARS);

export const hasSectionExceedingCharLimit = (
  sections: TemplateSection[]
): boolean => sections.some(isSectionOverCharLimit);

export const parseInstructions = (instructions: string): TemplateSection[] => {
  const sections = instructions
    .split("---")
    .filter((section) => section.trim());
  return sections.map((section, index) => {
    // Title: after ## in the same line (with or without space)
    const titleMatch = section.match(/^##\s*(.*)$/m);
    const title = titleMatch ? titleMatch[1].trim() : "";

    // Purpose: between lines containing purpose and prompt (case insensitive)
    const purposeMatch = section.match(/purpose[\s\S]*?prompt/is);
    const purpose = purposeMatch
      ? purposeMatch[0]
          .replace(/^.*purpose[^\n]*\n/i, "") // remove the entire purpose line=
          .replace(/\n[^\n]*prompt.*$/i, "") // remove everything from prompt line to end
          .trim() // remove spaces at the beginning and end
      : "";

    // Prompt: after the line with prompt
    const promptMatch = section.match(/prompt[\s\S]*$/i);
    const prompt = promptMatch
      ? promptMatch[0]
          .replace(/^.*prompt[^\n]*\n/i, "") // remove the entire prompt line
          .trim() // remove spaces at the beginning and end
      : "";

    const result = {
      title,
      purpose,
      prompt,
      order: index,
    };
    return result;
  });
};

// Convert API template sections into the markdown format accepted by the textarea
export const stringifyInstructions = (
  sections: Array<Pick<TemplateSection, "title" | "purpose" | "prompt">>
): string => {
  if (!sections || sections.length === 0) return "";
  return sections
    .sort((a, b) => {
      // Preserve existing order if a numeric order is provided; otherwise keep as-is
      const aOrder = (a as any).order ?? 0;
      const bOrder = (b as any).order ?? 0;
      return aOrder - bOrder;
    })
    .map((section) => {
      const title = section.title ?? "";
      const purpose = section.purpose ?? "";
      const prompt = section.prompt ?? "";
      return `## ${title}\n\n**Purpose:**\n${purpose}\n\n**Prompt:**\n${prompt}`;
    })
    .join("\n\n---\n\n");
};
